// function to check if anon-user exists
// function to create a user
// function to update all favorites button with correct state
// function to add a favorite
// function to remove a favorite
// function to get favorited state of id

import { map, Observable, ReplaySubject, tap } from "rxjs";
import { Failure } from "../../helpers/failure";
import { IFavoritesService } from "./favorites-interface";
import { getClicks$ } from "src/js/helpers/rxjs-helpers";
import localforage from "localforage";
import { groupBy } from "lodash-es";
import { pipe } from "fp-ts/lib/function";
import { array, option } from "fp-ts";
import { LanguageService } from "../language-service";
import { Toast } from "toaster-js";

class UGUser {
  static exists(): boolean {
    Failure("Not implemented");
  }

  static create(): void {}
}

interface FavoriteEntry {
  postId: number;
  time: Date;
}

const FAVORITES_LOG_KEY = "ug-favorites-list";
const FAVORITE_ELEMENT_SELECTOR = ".ug-favorit";
const LOADING_CLASS = "loading";
const IS_FAVORITED_CLASS = "active";
const IS_NOT_FAVORITED_CLASS = "deactive";
const POST_ID_ATTRIBUTE = "data-postid";
export class UGFavorites implements IFavoritesService {
  private favoritesList$: ReplaySubject<number[]>;
  private languageService: LanguageService;

  public constructor(languageService: LanguageService) {
    this.languageService = languageService;
    this.registerClickListener();
    this.favoritesList$ = new ReplaySubject(1);
    this.emitFavorites();
  }

  public get$(): Observable<number[]> {
    return this.favoritesList$;
  }
  public updateDom(): void {
    this.getFavoritePostIds().then((ids) => this.updateDomWithList(ids));
  }

  private updateDomWithList(currentFavorites: number[]): void {
    pipe(
      document.querySelectorAll(FAVORITE_ELEMENT_SELECTOR),
      Array.from<Element>,
      array.map(this.updateElement(currentFavorites))
    );
  }
  private updateElement =
    (currentFavoriteIds: number[]) => (element: Element) => {
      this.setState(element, this.IsElmFavoritted(element, currentFavoriteIds));
    };

  private IsElmFavoritted(elm: Element, currentFavoriteIds: number[]): boolean {
    const postId = this.parseFavoritesElement(elm).postId;
    const elmIsFavoritted = pipe(
      currentFavoriteIds,
      array.findFirst((f) => f == postId),
      option.isSome
    );
    return elmIsFavoritted;
  }

  private setState(elm: Element, elmIsFavoritted: boolean): void {
    elm.classList.remove(LOADING_CLASS);
    if (elmIsFavoritted) {
      elm.classList.remove(IS_NOT_FAVORITED_CLASS);
      elm.classList.add(IS_FAVORITED_CLASS);
    } else {
      elm.classList.remove(IS_FAVORITED_CLASS);
      elm.classList.add(IS_NOT_FAVORITED_CLASS);
    }
  }

  private async getFavoritePostIds(): Promise<number[]> {
    const l = await localforage.getItem<FavoriteEntry[]>(FAVORITES_LOG_KEY);
    if (l == null) {
      return [];
    }
    const entries = groupBy(l, (e) => e.postId); // group by postid
    const currentFavorites = Object.values(entries)
      .filter((x) => x.length % 2 == 1) // remove entries without odd number of entries
      .map((x) => x[x.length - 1]); // take the last entry for each group
    const currentFavoriteIds = currentFavorites.map((f) => f.postId); // take only postID
    return currentFavoriteIds;
  }

  private async emitFavorites(): Promise<void> {
    const favorites = await this.getFavoritePostIds();
    this.favoritesList$.next(favorites);
    this.updateDomWithList(favorites);
  }

  private async addFavorit(newEntry: FavoriteEntry): Promise<void> {
    let l = await localforage.getItem<FavoriteEntry[]>(FAVORITES_LOG_KEY);
    if (l == null) l = [];
    l.push(newEntry);
    await localforage.setItem(FAVORITES_LOG_KEY, l);
    this.emitFavorites();
    this.displayToastForEvent(newEntry);
  }

  private registerClickListener(): void {
    getClicks$(FAVORITE_ELEMENT_SELECTOR)
      .pipe(map(this.parseFavoritesElement), tap(this.addFavorit.bind(this)))
      .subscribe();
  }

  private parseFavoritesElement(e: Element): FavoriteEntry {
    const postIDStr = e.getAttribute(POST_ID_ATTRIBUTE);
    if (postIDStr == null) Failure(`Favorites element has no postID: ${e}`);
    const postID = parseInt(postIDStr);
    if (isNaN(postID)) Failure(`Favorites element has invalid postID: ${e}`);

    return {
      postId: postID,
      time: new Date(),
    };
  }

  private displayToastForEvent(favoritesEntry: FavoriteEntry): void {
    const postId = favoritesEntry.postId;
    const element = pipe(
      document.querySelector<Element>(
        `${FAVORITE_ELEMENT_SELECTOR}[${POST_ID_ATTRIBUTE}="${postId}"]`
      ),
      option.fromNullable,
      option.getOrElseW(() =>
        Failure(
          `Couldn't find favorites element "[${POST_ID_ATTRIBUTE}="${postId}"]"`
        )
      )
    );
    const isTidspunkt = element.classList.contains("favorittidspunkt");
    const wasAdded = element.classList.contains(IS_FAVORITED_CLASS);
    const textTable = this.languageService.getTranslations().toast;
    const text =
      isTidspunkt && wasAdded
        ? textTable.programRemove
        : isTidspunkt && !wasAdded
        ? textTable.programAdd
        : !isTidspunkt && wasAdded
        ? textTable.favoritesRemove
        : textTable.favoritesAdd;

    new Toast(text, Toast.TYPE_INFO, 3000);
  }
}
